import React, { useState, useEffect } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Menu,
  MenuItem, Divider, Typography, IconButton, Snackbar, Alert, useTheme, useMediaQuery
} from '@mui/material';
import { DashboardOutlinedIcon, ContactsOutlinedIcon, SurroundSoundOutlinedIcon, BarChartOutlinedIcon, ExtensionOutlinedIcon, SettingsOutlinedIcon, ArrowForwardIcon, MoreVertIcon } from 'icons/materialIcons';
import {
  FeedbackOutlinedIcon,
  LogoutOutlinedIcon,
  PersonOutlineOutlinedIcon,
  PowerOutlinedIcon,
  HelpOutlineOutlinedIcon,
  LocalOfferOutlinedIcon
} from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { useNavigate } from 'react-router-dom';
import { getUserById } from 'apis/authApi';
import RequestAFeatureModal from 'components/support/RequestAFeatureModal';
import { useAuth } from 'contexts/AuthContext';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { getDefaultCardByUserId } from 'apis/cardApi';

const Sidenav = ({ onMenuClick }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { logout, authState } = useAuth();
  const [profilePicture, setProfilePicture] = useState(avatarPlaceholder);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [isExpanded, setIsExpanded] = useState(!isSmallScreen);
  const userName = authState?.user?.fullName || 'User';
  const userId = authState?.user?.userId;



  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Effect to handle screen size changes
  useEffect(() => {
    setIsExpanded(!isSmallScreen);
  }, [isSmallScreen]);

  useEffect(() => {
    const fetchDefaultCardProfile = async () => {
      try {
        if (userId) {
          let lastError = null;
          const maxRetries = 3;
          const baseDelay = 1000;

          for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
              const defaultCard = await getDefaultCardByUserId(userId);
              if (defaultCard && defaultCard.profilePictureUrl) {
                setProfilePicture(defaultCard.profilePictureUrl);
                return;
              } else {
                return;
              }
            } catch (error) {
              lastError = error;

              if (attempt === maxRetries) {
                break;
              }

              const delay = baseDelay * Math.pow(2, attempt);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
      } catch (error) {
      }
    };

    fetchDefaultCardProfile();
  }, [userId]);

  const handleSidebarExpand = () => {
    if (isSmallScreen && !isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleMenuItemClick = (menu) => {
    if (isSmallScreen && !isExpanded) {
      setIsExpanded(true);
    }
    onMenuClick(menu);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSubmitSuccess = () => {
    setSnackbar({
      open: true,
      message: 'Feature request sent successfully!',
      severity: 'success'
    });
  };

  const handleSubmitError = (errorMessage) => {
    setSnackbar({
      open: true,
      message: errorMessage,
      severity: 'error'
    });
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAppClick = () => {
  };

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleSubscribe = () => {
    handleClose();
    navigate(`/dashboard/subscribe`);
  };

  useEffect(() => {
    fetchUserData();
  }, [authState?.user?.userId]);


  const fetchUserData = async () => {
    try {
      const idToken = localStorage.getItem('authToken');
      if (!idToken) {
        throw new Error('No auth token found');
      }
      const response = await getUserById(userId, idToken);
      setUserData(response.data);
    } catch (error) {
    }
  };
  return (
    <>
      <Drawer
        sx={{
          width: isSmallScreen ? (isExpanded ? 180 : 56) : 180,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isSmallScreen ? (isExpanded ? 180 : 56) : 180,
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ paddingTop: "30px", paddingBottom: "10px", display: 'flex', alignItems: 'center', marginLeft: "20px", justifyContent: 'start' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#333',
                fontSize: isExpanded ? '1.1rem' : '0.9rem',
                letterSpacing: '0.5px',
                textTransform: 'none',
                textAlign: 'center'
              }}>
              Touch
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <List>
              {[
                { text: 'My Cards', icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('mycards') },
                { text: 'Contacts', icon: <ContactsOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('contacts') },
                { text: 'Accessories', icon: <SurroundSoundOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('accessories') },
                { text: 'Insights', icon: <BarChartOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('insights') },
                { text: 'Support', icon: <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('support') },
                { text: 'Settings', icon: <SettingsOutlinedIcon sx={{ fontSize: 20 }} />, onClick: () => handleMenuItemClick('settings') }
              ].map((item) => (
                <ListItem
                  key={item.text}
                  onClick={item.onClick}
                  sx={{
                    justifyContent: (!isSmallScreen || isExpanded) ? 'flex-start' : 'center',
                    px: (!isSmallScreen || isExpanded) ? 2 : 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: (!isSmallScreen || isExpanded) ? 40 : 'auto',
                      justifyContent: 'center'
                    }}
                    onClick={(e) => {
                      if (!isExpanded) {
                        e.stopPropagation();
                        handleSidebarExpand();
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {(!isSmallScreen || isExpanded) && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        sx: {
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: "#4f4f4f",
                          marginLeft: 0
                        }
                      }}
                    />
                  )}
                </ListItem>
              ))}
            </List>

            {isSmallScreen && !isExpanded && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                mb: 2
              }}>
                <Box
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={handleSidebarExpand}
                >
                  <img
                    src={profilePicture}
                    alt="User Avatar"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = avatarPlaceholder;
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {(!isSmallScreen || isExpanded) && (
              <>
                <Divider />
                <Box sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={profilePicture}
                      alt="User Avatar"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = avatarPlaceholder;
                      }}
                    />
                    <Box sx={{ marginLeft: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                        {userName || userData?.fullName}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: "0.7rem", fontWeight: 600 }}>
                        USER
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={handleMenuClick}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Divider />
              </>
            )}
          </Box>
        </Box>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              padding: '8px',
              borderRadius: '8px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <MenuItem onClick={() => { handleMenuClose(); handleOpenModal(); }}>
            <FeedbackOutlinedIcon sx={{ fontSize: 20, color: '#757575', marginRight: 1 }} />
            Request a Feature
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
            <LogoutOutlinedIcon sx={{ fontSize: 20, color: '#757575', marginRight: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Drawer>

      <RequestAFeatureModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitError={handleSubmitError}
      />

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default Sidenav;